<template>
  <Form ref="refFormCompanies">
    <v-skeleton-loader v-if="detailview.loading" type="heading, card, card, card" />
    <FormCompanies v-else v-model="formModel" :locationItems="locationItems" :isDetailview="true" :mode="detailview.mode" />
  </Form>
</template>

<script>
import Form from "@/components/forms/Form.vue";
import FormCompanies from "./Form.vue";

import { defaultFormModel, prepareCompanyForLoad, prepareCompanyForSave, prepareLocationItem } from "./model";
import { useAuthStore } from "@/stores/authStore.ts";
import { useDetailviewStore } from "@/stores/detailviewStore";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import { getRequestGeneric, putRequestGeneric, postRequestGeneric } from "@/services/backendApiGeneric";

import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

export default {
  name: "DetailviewComponentCompanies",

  components: {
    Form,
    FormCompanies,
  },

  data() {
    return {
      formModel: cloneDeep(defaultFormModel),
      lastSavedModel: null,

      locationItems: [],
    };
  },

  created() {
    this.detailview = useDetailviewStore();
  },

  mounted() {
    this.detailview.onLoad = this.onLoad;
    this.detailview.onSave = this.onSave;
    this.detailview.onCreate = this.onCreate;
    this.detailview.onUndo = this.onUndo;
    this.detailview.onLoad();
  },

  beforeUnmount() {
    this.detailview.onLoad = null;
    this.detailview.onSave = null;
    this.detailview.onCreate = null;
    this.detailview.onUndo = null;
    this.detailview.hasUnsavedChanges = () => false;
  },

  watch: {
    formModel: {
      handler() {
        if (this.detailview.loading || this.detailview.mode !== "edit") return;
        this.detailview.saveBarVisible = this.hasUnsavedChanges();
        if (!this.detailview.saveBarVisible) this.detailview.clearError();
      },
      deep: true,
    },
  },

  computed: {
    authStore() {
      return useAuthStore();
    },
  },

  methods: {
    async onLoad() {
      const token = ++this.detailview.loadToken;
      this.detailview.loading = true;
      this.detailview.clearError();
      this.detailview.saveBarVisible = false;

      try {
        const id = this.detailview.selectedItem?.id;

        if (this.detailview.mode === "create") {
          this.formModel = { ...cloneDeep(defaultFormModel), ...cloneDeep(this.detailview.selectedItem) };
          this.lastSavedModel = cloneDeep(this.formModel);

          this.detailview.hasUnsavedChanges = this.hasUnsavedChanges;
          this.detailview.title = this.formModel.name;

          this.detailview.loading = false;

          return;
        }

        if (!id) return;

        const [locations, data] = await Promise.all([
          getRequestGeneric(`/protected/locations/load-locations?idCompany=${this.detailview.selectedItem.id}`),
          getRequestGeneric(`/protected/companies/load-company/${id}`),
        ]);

        if (token !== this.detailview.loadToken) return;

        this.locationItems = locations.map(prepareLocationItem);

        this.formModel = prepareCompanyForLoad(data);
        this.lastSavedModel = cloneDeep(this.formModel);

        this.detailview.hasUnsavedChanges = this.hasUnsavedChanges;
        this.detailview.title = this.formModel.name;
      } catch (error) {
        if (token === this.detailview.loadToken) {
          useSnackbarStore().showSnackbar({
            message: error.message,
            type: "error",
          });
        }
      } finally {
        if (token === this.detailview.loadToken) {
          this.$nextTick(() => {
            this.detailview.loading = false;
          });
        }
      }
    },

    async onSave() {
      this.detailview.clearError();
      this.detailview.loadingSave = true;

      try {
        const form = this.$refs.refFormCompanies;
        const valid = await form.validateForm();

        if (!valid) {
          this.detailview.setError("Bitte prüfen Sie die rot umrandeten Felder und korrigieren Sie diese.");
          return false;
        }

        const payload = await prepareCompanyForSave(this.formModel);
        await putRequestGeneric(`/protected/companies/save/${this.detailview.selectedItem.id}`, payload, true);
        this.lastSavedModel = cloneDeep(this.formModel);

        await this.detailview.onSaved();

        useSnackbarStore().showSnackbar({
          message: "Änderungen gespeichert.",
          type: "success",
        });

        this.detailview.saveBarVisible = false;
        return true;
      } catch (error) {
        this.detailview.setError(error.message);
        return false;
      } finally {
        this.detailview.loadingSave = false;
      }
    },

    async onCreate() {
      this.detailview.clearError();
      this.detailview.loadingSave = true;

      try {
        const form = this.$refs.refFormCompanies;
        const valid = await form.validateForm();

        if (!valid) {
          this.detailview.setError("Bitte prüfen Sie die rot umrandeten Felder und korrigieren Sie diese.");
          return false;
        }

        const payload = await prepareCompanyForSave(this.formModel);
        const created = await postRequestGeneric(`/protected/companies/create/${this.detailview.selectedItem.idOrganization}`, payload, true);
        // Spec 2026-10-21: Mit der neuen ID führt das Organigramm in
        // „Abrechnung einrichten“.
        await this.detailview.onCreated(created?.id);

        useSnackbarStore().showSnackbar({
          message: "Unternehmen angelegt.",
          type: "success",
        });

        this.detailview.close();
        return true;
      } catch (error) {
        this.detailview.setError(error.message);
        return false;
      } finally {
        this.detailview.loadingSave = false;
      }
    },

    async onUndo() {
      await this.onLoad();
    },

    hasUnsavedChanges() {
      return !isEqual(this.formModel, this.lastSavedModel);
    },
  },
};
</script>
